import { resolveEventInstant } from './eventTime';

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function formatEventTime(isoString, eventTimezone) {
  if (!isoString) return '';
  const tz = eventTimezone || 'UTC';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(resolveEventInstant(isoString, tz));
  } catch {
    return isoString;
  }
}

// Same instant as formatEventTime, split into separate date/time lines for
// layouts that stack them vertically to save horizontal space (e.g. EventList.jsx).
export function formatEventDateParts(isoString, eventTimezone) {
  if (!isoString) return { date: '', time: '' };
  const tz = eventTimezone || 'UTC';
  try {
    const instant = resolveEventInstant(isoString, tz);
    const date = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: 'short', day: 'numeric',
    }).format(instant);
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(instant);
    return { date, time };
  } catch {
    return { date: isoString, time: '' };
  }
}

export function formatLocalOffset(isoString, eventTimezone) {
  if (!isoString || !eventTimezone) return '';
  if (eventTimezone === VIEWER_TZ) return '';
  try {
    const local = new Intl.DateTimeFormat('en-US', {
      timeZone: VIEWER_TZ,
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(resolveEventInstant(isoString, eventTimezone));
    return `(${local} your time)`;
  } catch {
    return '';
  }
}

// Server-generated fields (created_at, sent_at, checked_at, ...) are genuinely UTC
// instants (Python datetime.utcnow() / MySQL NOW()) but serialized naive, same as
// event.start_datetime — new Date(naiveUtcString) misparses as browser-local here
// too. Unlike event times, these need no zone resolution, just an explicit UTC marker.
export function parseUtcTimestamp(naiveIso) {
  if (!naiveIso) return null;
  // Only a naive "...THH:MM:SS"-shaped string needs the Z appended — a bare
  // date-only "YYYY-MM-DD" string already parses as UTC midnight per spec, and
  // appending Z to one is not reliably supported across browsers.
  const isNaiveDatetime = /T\d{2}:\d{2}/.test(naiveIso) && !/Z$|[+-]\d{2}:\d{2}$/.test(naiveIso);
  return new Date(isNaiveDatetime ? `${naiveIso}Z` : naiveIso);
}

// A DATE-only "YYYY-MM-DD" is a calendar date, not an instant — parsed as UTC
// midnight and rendered in a US browser zone it shows the previous day.
export function formatShortDate(val) {
  if (!val) return '—';
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return new Date(`${val}T00:00:00Z`).toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
  }
  const d = parseUtcTimestamp(val);
  return Number.isNaN(d.getTime()) ? val : d.toLocaleDateString('en-US', opts);
}
